import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
// DM Sans is the single dashboard typeface (Claude Design). Load every
// weight the design uses once, so pages no longer depend on local imports.
import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/500.css";
import "@fontsource/dm-sans/600.css";
import "@fontsource/dm-sans/700.css";
import "./index.css";
import App from "./App";
import { isEventPreCheckInPath } from "./components/pages/Events/EventPreCheckInPage";
import reportWebVitals from "./reportWebVitals";
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
} from "react-router-dom";
import { Amplify } from "aws-amplify";
import { ThemeProvider } from "@mui/material/styles";
import { UserTypeProvider } from "./contexts/UserTypeContext";
import { cmpTheme } from "./theme";
import * as Sentry from "@sentry/react";
import "react-toastify/dist/ReactToastify.css";
import { isSecureCheckoutReturnPath } from "./components/pages/Invoicing/SecureCheckoutReturnPage";

const isPublicPreCheckIn = isEventPreCheckInPath(window.location.pathname);
if (isPublicPreCheckIn) {
  const referrer = document.createElement("meta");
  referrer.name = "referrer";
  referrer.content = "no-referrer";
  document.head.appendChild(referrer);
}

Sentry.init({
  enabled: process.env.NODE_ENV === "production" && !isPublicPreCheckIn,
  dsn: "https://b680d33a68a655afeb4479a9b4c64f6f@o4507950792704000.ingest.us.sentry.io/4508770382249984",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

if (!isSecureCheckoutReturnPath(window.location.pathname) && !isPublicPreCheckIn) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: process.env.REACT_APP_AWS_USER_POOL_ID as string,
        userPoolClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID as string,
      },
    },
  });
}

// scroll to top when going to a new page
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

// The wildcard route preserves the existing App and LoggedInApp route trees.
// A data-router root gives shared form guards the supported useBlocker API,
// without adding duplicate browser-history sentinel entries.
const router = createBrowserRouter([
  {
    path: "*",
    element: (
      <>
        <ScrollToTop />
        <App />
      </>
    ),
  },
]);

root.render(
  <React.StrictMode>
    <ThemeProvider theme={cmpTheme}>
      <UserTypeProvider>
        <RouterProvider router={router} />
      </UserTypeProvider>
    </ThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
